import React from 'react';
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {Edit} from '@mui/icons-material';
import {useQuery} from '@apollo/client';
import {SERVICES_QUERY, useAppointmentManagementGraphQL} from '../../service/useAppointmentGraphQL';
import ServiceEditModal from './ServiceEditModal';
import Loading from '../../components/common/Loading';
import {useTranslation} from 'react-i18next';
import AppSnackbar from '../../common/AppSnackbar';

function euro(v) {
    if (v == null) return '';
    const num = Number(v);
    if (Number.isNaN(num)) return String(v);
    return `€${num.toFixed(2)}`;
}

function speciesLabelFactory(t) {
    return (s) => {
        if (!s) return t('allAnimals');
        if (s === 'CAT') return t('cat');
        if (s === 'DOG') return t('dog');
        return s;
    };
}

function ServicesList() {
    const {t} = useTranslation();
    const {useSetServiceActive} = useAppointmentManagementGraphQL();
    const [setServiceActive, {loading: toggling}] = useSetServiceActive();

    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [severity, setSeverity] = React.useState('success');
    const [modalOpen, setModalOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null); // row or null

    const [filters, setFilters] = React.useState({active: 'all', species: 'all'});
    const variables = React.useMemo(() => {
        if (filters.active === 'true') return {active: true};
        if (filters.active === 'false') return {active: false};
        return {active: null};
    }, [filters.active]);

    const {data, loading, error, refetch} = useQuery(SERVICES_QUERY, {variables});
    const items = React.useMemo(() => {
        const list = data?.services || [];
        if (filters.species === 'all') return list;
        return list.filter(s => (s.species || 'all') === filters.species);
    }, [data, filters.species]);

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setModalOpen(true);
    };

    const onSaved = async () => {
        await refetch();
        setSnackbarMessage(t('serviceSaved'));
        setSeverity('success');
        setOpenSnackbar(true);
    };

    const onToggleActive = async (row, next) => {
        try {
            await setServiceActive({variables: {uuid: row.uuid, active: next}});
            await refetch();
        } catch (e) {
            setSnackbarMessage(t('toggleActiveFailed'));
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    return (
        <Box sx={{width: '100%'}}>
            {/* Header / Actions */}
            <Grid container spacing={2} alignItems="center" sx={{mb: 2}}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('show')}
                        value={filters.active}
                        onChange={(e) => setFilters((f) => ({...f, active: e.target.value}))}
                    >
                        <MenuItem value="all">{t('all')}</MenuItem>
                        <MenuItem value="true">{t('active')}</MenuItem>
                        <MenuItem value="false">{t('inactive')}</MenuItem>
                    </TextField>
                </Grid>
                {/*<Grid item xs={12} sm={3}>*/}
                {/*    <TextField*/}
                {/*        select*/}
                {/*        fullWidth*/}
                {/*        size="small"*/}
                {/*        label={t('animalType')}*/}
                {/*        value={filters.species}*/}
                {/*        onChange={(e) => setFilters((f) => ({...f, species: e.target.value}))}*/}
                {/*    >*/}
                {/*        <MenuItem value="all">{t('all')}</MenuItem>*/}
                {/*        <MenuItem value="CAT">{t('cat')}</MenuItem>*/}
                {/*        <MenuItem value="DOG">{t('dog')}</MenuItem>*/}
                {/*    </TextField>*/}
                {/*</Grid>*/}
                <Grid item xs={12} sm sx={{textAlign: {xs: 'left', sm: 'right'}}}>
                    <Button variant="contained" onClick={openCreate} data-testid="add-service-button">+ {t('newService')}</Button>
                </Grid>
            </Grid>

            {/* Content */}
            <Paper variant="outlined">
                {loading && (
                    <Box sx={{p: 3, display: 'flex', gap: 1, alignItems: 'center'}}>
                        <CircularProgress size={20}/>
                        <Typography>{t('loadingServices')}</Typography>
                    </Box>
                )}
                {!loading && error && (
                    <Box sx={{p: 2}}>
                        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refetch()}>{t('retry')}</Button>}>
                            {t('servicesLoadFailed')}
                        </Alert>
                    </Box>
                )}
                {!loading && !error && (
                    <Table size="small" aria-label={t('servicesTableAria')}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('name')}</TableCell>
                                <TableCell>{t('description')}</TableCell>
                                <TableCell align="right">{t('durationShort')}</TableCell>
                                <TableCell align="right">{t('bufferBefore')}</TableCell>
                                <TableCell align="right">{t('bufferAfter')}</TableCell>
                                <TableCell align="right">{t('price')}</TableCell>
                                <TableCell>{t('animalType')}</TableCell>
                                <TableCell>{t('active')}</TableCell>
                                <TableCell>{t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((row) => (
                                <TableRow key={row.uuid} hover>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell sx={{maxWidth: 360, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden'}}>{row.description}</TableCell>
                                    <TableCell align="right">{row.durationMinutes}</TableCell>
                                    <TableCell align="right">{row.bufferBeforeMinutes || 0}</TableCell>
                                    <TableCell align="right">{row.bufferAfterMinutes || 0}</TableCell>
                                    <TableCell align="right">{euro(row.price)}</TableCell>
                                    <TableCell>{speciesLabelFactory(t)(row.species)}</TableCell>
                                    <TableCell>
                                        <Switch
                                            size="small"
                                            checked={!!row.active}
                                            onChange={(e) => onToggleActive(row, e.target.checked)}
                                            disabled={toggling}
                                            inputProps={{'aria-label': t('toggleActiveFor', {name: row.name, state: row.active ? t('off') : t('on')}), 'data-testid': `toggle-service-active-${row.name}`}}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" aria-label={t('edit')} onClick={() => openEdit(row)} data-testid={`edit-service-button-${row.name}`}>
                                            <Edit fontSize="inherit"/>
                                        </IconButton>
                                        {/* Delete icon verborgen tenzij backend dit ondersteunt */}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Typography variant="body2" sx={{p: 2}}>{t('noServicesFound')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Create/Edit Modal */}
            <ServiceEditModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                initial={editing}
                onSaved={onSaved}
            />

            <AppSnackbar
                openSnackbar={openSnackbar}
                snackbarMessage={snackbarMessage}
                setOpenSnackbar={setOpenSnackbar}
                severity={severity}
            />
        </Box>
    );
}

export default withAuthenticationRequired(ServicesList, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login"
});
